import { asField } from 'informed';
import { objectWithoutProperties as _objectWithoutProperties, toConsumableArray as _toConsumableArray, createForOfIteratorHelper as _createForOfIteratorHelper, slicedToArray as _slicedToArray } from '../../../_virtual/_rollupPluginBabelHelpers.js';
import themes from '../../theming/themes.js';
import React, { useRef, createContext } from 'react';
import withDefaultTheme from '../../hocs/withDefaultTheme.js';
import _InputGroup from '../InputGroup/index.js';

var _excluded = ["fieldState", "fieldApi"];
var CheckboxGroupContext = /*#__PURE__*/createContext({});
/* ~ CheckboxGroup Context Function Definitions ~ */
var handleChildBlur = function handleChildBlur(onBlur, setTouched) {
  return function (e) {
    setTouched(true);
    if (onBlur) {
      onBlur(e);
    }
  };
};
var handleSelectAll = function handleSelectAll(currentCheckboxValues, checkboxFieldMap, setValue) {
  return function (checked) {
    // grab working copy of CheckboxGroup values
    var newValues = [];
    if (!!currentCheckboxValues) {
      newValues = _toConsumableArray(currentCheckboxValues);
    }
    var _iterator = _createForOfIteratorHelper(checkboxFieldMap.current.entries()),
      _step;
    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var _step$value = _slicedToArray(_step.value, 2),
          fieldId = _step$value[0],
          fieldControls = _step$value[1];
        // directly set the value on form only if necessary
        if (fieldControls.fieldApi.getValue() !== checked) {
          fieldControls.fieldApi.setValue(checked);
        }
        // add or remove fieldId from WIP CheckboxGroup values
        var index = newValues.indexOf(fieldId);
        if (checked) {
          if (index < 0) {
            newValues.push(fieldId);
          }
        } else {
          if (index >= 0) {
            newValues.splice(index, 1);
          }
        }
      }
      // save final chekboxGroup values
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
    setValue(newValues);
  };
};
var handleChildValueChange = function handleChildValueChange(value, checkboxFieldMap, selectAllCheckboxFieldApiRef, onChange, setValue) {
  return function (field) {
    var newValue;
    if (!!value) {
      if (field.value) {
        // Add fieldname to array
        newValue = _toConsumableArray(value);
        newValue.push(field.fieldName);
      } else {
        // remove fieldname from array
        newValue = _toConsumableArray(value);
        var index = newValue.indexOf(field.fieldName);
        if (index >= 0) {
          newValue.splice(index, 1);
        }
      }
    } else {
      // establish new array with single value
      newValue = field.value ? [field.fieldName] : value;
    }
    /* set value and handle onChange */
    setValue(newValue);
    if (onChange) {
      onChange(value);
    }
    /* Handle selectAllCheckbox behavior */
    detectAllSelected(checkboxFieldMap, selectAllCheckboxFieldApiRef);
  };
};
/* ~ END CheckboxGroup Context Function Definitions ~ */
/**
 * inspects fields in the group to determine if selectAllCheckbox should be selected or not.
 * To be called on checkbox registration & deregistration to ensure the group initializes and updates
 * the selectAllCheckbox correctly as it renders or as elements are added/removed.
 */
var detectAllSelected = function detectAllSelected(checkboxFieldMap, selectAllCheckboxFieldApiRef) {
  // only do this if the group has a selectAllCheckbox
  if (selectAllCheckboxFieldApiRef.current && checkboxFieldMap.current) {
    // Inspect the values to determine correct selectAllCheckbox value
    var newSelectAllValue = true;
    var _iterator2 = _createForOfIteratorHelper(checkboxFieldMap.current.values()),
      _step2;
    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var checkboxFieldControl = _step2.value;
        if (!checkboxFieldControl.fieldApi.getValue()) {
          newSelectAllValue = false;
        }
      }
      // set the new selectAllCheckbox value if needed
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }
    if (selectAllCheckboxFieldApiRef.current.getValue() !== newSelectAllValue) {
      selectAllCheckboxFieldApiRef.current.setValue(newSelectAllValue);
    }
  }
};
var CheckboxGroup = function CheckboxGroup(_ref) {
  var fieldState = _ref.fieldState,
    fieldApi = _ref.fieldApi,
    props = _objectWithoutProperties(_ref, _excluded);
  var value = fieldState.value,
    error = fieldState.error;
  var setValue = fieldApi.setValue,
    setTouched = fieldApi.setTouched;
  var onChange = props.onChange,
    onBlur = props.onBlur,
    label = props.label,
    hideLabel = props.hideLabel,
    _props$theme = props.theme,
    theme = _props$theme === void 0 ? themes["default"] : _props$theme,
    _props$language = props.language,
    language = _props$language === void 0 ? 'en' : _props$language,
    _props$optional = props.optional,
    optional = _props$optional === void 0 ? false : _props$optional,
    field = props.field;
  var selectAllCheckboxFieldApiRef = useRef();
  var checkboxFieldMap = useRef(new Map());
  /* ~ More CheckboxGroup Context Function Definitions ~ */
  var registerFieldToCheckboxGroup = function registerFieldToCheckboxGroup(id, fieldControls) {
    if (checkboxFieldMap.current) {
      checkboxFieldMap.current.set(id, fieldControls);
    }
    detectAllSelected(checkboxFieldMap, selectAllCheckboxFieldApiRef);
  };
  var deregisterFieldToCheckboxGroup = function deregisterFieldToCheckboxGroup(id) {
    if (checkboxFieldMap.current) {
      checkboxFieldMap.current["delete"](id);
    }
    detectAllSelected(checkboxFieldMap, selectAllCheckboxFieldApiRef);
  };
  var registerSelectAllCheckbox = function registerSelectAllCheckbox(childRef) {
    selectAllCheckboxFieldApiRef.current = childRef;
  };
  var deregisterSelectAllCheckbox = function deregisterSelectAllCheckbox(childRef) {
    if (selectAllCheckboxFieldApiRef.current) {
      selectAllCheckboxFieldApiRef.current = undefined;
    }
  }; // TODO: move all outide CheckboxGroup const definition?
  /* ~ END  More CheckboxGroup Context Function Definitions ~ */
  var context = {
    deregisterFieldToCheckboxGroup: deregisterFieldToCheckboxGroup,
    deregisterSelectAllCheckbox: deregisterSelectAllCheckbox,
    error: error,
    handleSelectAll: handleSelectAll(value, checkboxFieldMap, setValue),
    onBlur: handleChildBlur(onBlur, setTouched),
    onChange: handleChildValueChange(value, checkboxFieldMap, selectAllCheckboxFieldApiRef, onChange, setValue),
    registerFieldToCheckboxGroup: registerFieldToCheckboxGroup,
    registerSelectAllCheckbox: registerSelectAllCheckbox
  };
  return /*#__PURE__*/React.createElement(CheckboxGroupContext.Provider, {
    value: context
  }, /*#__PURE__*/React.createElement(_InputGroup, {
    label: label,
    hideLabel: hideLabel,
    error: error,
    theme: theme,
    language: language,
    optional: optional,
    field: field
  }, props.children));
};
// @ts-ignore
var _CheckboxGroup = withDefaultTheme(asField(CheckboxGroup));

export default _CheckboxGroup;
export { CheckboxGroupContext };
